import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.athenex.xyz";
const ADMIN_API_PATH = "/admin";

interface AdminAuthContextProps {
  isAdminAuthenticated: boolean;
  isAdminLoading: boolean;
  adminAuthError: string | null;
  adminLogin: (email: string, password: string) => Promise<boolean>;
  adminLogout: () => void;
  getAdminAuthHeaders: () => Record<string, string>;
}

const AdminAuthContext = createContext<AdminAuthContextProps | undefined>(undefined);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState<boolean>(false);
  const [isAdminLoading, setIsAdminLoading] = useState<boolean>(true);
  const [adminAuthError, setAdminAuthError] = useState<string | null>(null);

  // Initialize auth state from localStorage on app load
  useEffect(() => {
    const checkAdminAuth = async () => {
      setIsAdminLoading(true);
      
      const storedAdminToken = localStorage.getItem('admin-token');
      const storedAdminApiKey = localStorage.getItem('admin-api-key');
      
      if (storedAdminToken || storedAdminApiKey) {
        try {
          // Try to access a protected admin endpoint to verify token/API key
          const headers: Record<string, string> = {};
          
          // If we have an API key, use that (preferred)
          if (storedAdminApiKey) {
            headers['X-API-Key'] = storedAdminApiKey; // Match the example code header name
          } 
          // Fallback to token if no API key
          else if (storedAdminToken) {
            headers['Authorization'] = `Bearer ${storedAdminToken}`;
          }
          
          await axios.get(
            `${API_BASE_URL}${ADMIN_API_PATH}/users`,
            { headers }
          );
          
          console.log("Admin auth initialized with stored credentials");
          setIsAdminAuthenticated(true);
          setAdminAuthError(null);
        } catch (error) {
          console.error("Stored admin credentials invalid:", error);
          // Clear the invalid credentials
          localStorage.removeItem('admin-token');
          localStorage.removeItem('admin-api-key');
          setIsAdminAuthenticated(false);
          setAdminAuthError("Admin session expired. Please login again.");
        }
      } else {
        setIsAdminAuthenticated(false);
      }
      
      setIsAdminLoading(false);
    };
    
    checkAdminAuth();
  }, []);

  // Function to retrieve API key from database - updated to match the example code
  const retrieveApiKey = async () => {
    // Get token from localStorage
    const token = localStorage.getItem('admin-token');
    
    if (!token) {
      console.error("Cannot retrieve API key: missing token");
      return false;
    }
    
    try {
      console.log("Retrieving API key for admin user");
      
      // Use the correct endpoint and headers as shown in the example
      const response = await fetch(`${API_BASE_URL}${ADMIN_API_PATH}/auth/api-key`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        console.error(`Failed to retrieve API key: ${response.status} ${response.statusText}`);
        // For debugging
        const errorText = await response.text();
        console.error("Error details:", errorText);
        return false;
      }
      
      const data = await response.json();
      console.log("API key response:", data);

    // Handle different possible response formats
    if (data.api_key) {
      console.log("Found API key");
      localStorage.setItem('admin-api-key', data.api_key);
      return true;
    } else if (data.key) {
      console.log("Found API key (alternate format)");
      localStorage.setItem('admin-api-key', data.key);
      return true;
    } else {
      console.error("API key not found in response:", data);
      return false;
    }
  } catch (error) {
    console.error("Error retrieving API key:", error);
    return false;
  }
};

  const adminLogin = async (email: string, password: string): Promise<boolean> => {
    setIsAdminLoading(true);
    setAdminAuthError(null);
    
    try {
      console.log("Admin login attempt with email:", email);
      
      // Login to get JWT token (Step 1 from example)
      const authData = {
        username: email, // Keep 'username' field name as expected by backend
        password: password
      };
      
      console.log("Authenticating to get JWT token...");
      
      const response = await fetch(`${API_BASE_URL}${ADMIN_API_PATH}/auth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(authData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Token auth failed: ${response.status}`, errorText);
        throw new Error(`Authentication failed: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.access_token) {
        // Save token and email for future requests
        localStorage.setItem('admin-token', data.access_token);
        localStorage.setItem('admin-email', email);
        setIsAdminAuthenticated(true);
        
        // After successful login, retrieve the API key (Step 2 from example)
        const apiKeySuccess = await retrieveApiKey();
        
        if (!apiKeySuccess) {
          console.warn("Authentication succeeded but API key retrieval failed. Some functionality may be limited.");
        }
        
        setIsAdminLoading(false);
        console.log("Authentication successful");
        return true;
      } else {
        throw new Error("No access token in response");
      }
    } catch (error: any) {
      console.error("Admin login error:", error.message);
      
      // More detailed error message
      const errorMessage = "Login failed. Please check your credentials.";
      setAdminAuthError(errorMessage);
      setIsAdminAuthenticated(false);
      setIsAdminLoading(false);
      return false;
    }
  };

  const adminLogout = () => {
    // Clear stored credentials
    localStorage.removeItem('admin-token');
    localStorage.removeItem('admin-api-key');
    localStorage.removeItem('admin-email');
    setIsAdminAuthenticated(false);
    setAdminAuthError(null);
  };
  
  const getAdminAuthHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('admin-token');
    const apiKey = localStorage.getItem('admin-api-key');
    const adminEmail = localStorage.getItem('admin-email');
    
    // Create headers based on what's available and what the backend expects
    // Following the pattern from the provided example code
    const headers: Record<string, string> = {};
    
    // If we have an API key, use it as the primary auth method (Step 3 from example)
    if (apiKey) {
      headers["X-API-Key"] = apiKey; // Use same header name as in the example
    }
    // Fallback to JWT token if no API key is available
    else if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    
    // Include admin email if available
    if (adminEmail) {
      headers["X-Admin-Email"] = adminEmail;
    }
    
    return headers;
  };
  
  const value = {
    isAdminAuthenticated,
    isAdminLoading,
    adminAuthError,
    adminLogin,
    adminLogout,
    getAdminAuthHeaders
  };
  
  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export default AdminAuthContext;